/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CommonStatusStep,
  IAPIServicesOpenItem,
  IApiSubServiceItem,
  RbacAdminPermObjectKey,
} from '../../../model/api-modules';
import { AutoTable } from '../../../components/auto-table';
import getFormatMessage from '../../../locales/get-format-message';
import { CommonButton } from '../../../components/button/main';
import { ModalWrap } from '../../../components/modal-wrap';
import { messageError, messageSuccess } from '../../../components/message';
import {
  closeService,
  offlineOldService,
  serviceAdminOpenList,
} from '../../../api';
import { useEffect, useState } from 'react';
import { IAPIresponse } from '../../../model';
import { getUserNameBySite } from '../lib/columns';
import { getLocalTime } from '../../../lib';
import { wrapRequestArea } from '../../../lib/http/lib';
import { getGameManagementList } from '../../../api/admin/game';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducers';
import { AdminPermCheck } from '../components/admin-permission-check';

interface IProps {
  readonly site?: string;
  readonly gameID: string;
  readonly envID: number;
  readonly services: IAPIServicesOpenItem[];
  readonly onChange?: () => void;
}

const CAN_NOT_CLOSE_ONLY: Record<string, string[]> = {};

export const SubServiceChoose = (props: IProps) => {
  const { site, services, gameID, envID, onChange } = props;

  const [list, setList] = useState<readonly IApiSubServiceItem[]>([]);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const aggRes = await wrapRequestArea(getGameManagementList, site);
    const result = await wrapRequestArea(
      () => serviceAdminOpenList(gameID, envID),
      site
    );
    const listRes = await Promise.all(
      services.map(async (service) => {
        const matchedService = result.find(
          (item) => item.ServiceID === service.ServiceID
        );
        if (!matchedService) {
          return [];
        }
        return (
          matchedService.SubServiceName?.map((item) => {
            return {
              ...item,
              serviceName: service.ServiceName,
              service:
                aggRes?.serviceOpenList.find(
                  (allServiceItem) => allServiceItem.id === item.tableID
                ) || service,
            };
          }) || [
            {
              serviceName: service.ServiceName,
              subserviceName: service.ServiceName || '',
              tableID: service.id,
              status: matchedService.Status,
              service:
                aggRes?.serviceOpenList.find(
                  (allServiceItem) => allServiceItem.id === service.id
                ) || service,
            },
          ]
        );
      })
    );
    const serviceList = listRes.flat();
    setList(serviceList);
  };

  const handleOpenClose = async (record: IApiSubServiceItem) => {
    ModalWrap.warn({
      title:
        record.status === CommonStatusStep.Resolve ? '关闭服务' : '打开服务',
      children:
        record.status === CommonStatusStep.Resolve
          ? '确定关闭此服务么?'
          : '确定打开此服务么?',
      oKText: getFormatMessage('yes'),
      cancelText: getFormatMessage('no'),
      async onOk() {
        let res: IAPIresponse | null | boolean = false;
        const targetStatus =
          record.status === CommonStatusStep.Resolve
            ? CommonStatusStep.Reject
            : CommonStatusStep.Resolve;
        const serviceName = record.service?.ServiceName || '';
        const needBatchClose = CAN_NOT_CLOSE_ONLY[serviceName] || [];
        const operateItems = needBatchClose.includes(record.subserviceName)
          ? list.filter((item) =>
              needBatchClose.includes(item.subserviceName)
            ) || []
          : [record];

        for (const operateItem of operateItems) {
          res = await operateOpenClose(operateItem, targetStatus);
        }

        if (!res) {
          messageError(getFormatMessage('failed'));
          return false;
        }

        messageSuccess(getFormatMessage('successfully'));
        onChange?.();
        init();
        return true;
      },
    });
  };

  const operateOpenClose = async (
    record: IApiSubServiceItem,
    targetStatus: CommonStatusStep
  ) => {
    if (
      !record.tableID &&
      record.service?.ServiceID &&
      record.service?.GameID &&
      record.service?.EnvID
    ) {
      return await offlineOldService({
        serviceID: record.service?.ServiceID,
        gameID: record.service?.GameID,
        envID: record.service?.EnvID,
        subServiceName: record.subserviceName,
        status: targetStatus,
      });
    }

    if (record.tableID) {
      return await closeService(record.tableID, targetStatus);
    }

    return false;
  };

  return (
    <AutoTable
      list={list}
      configFields={[
        {
          name: '服务',
          key: 'serviceName',
          tableExtension: {
            renderRow(_key: any, _val: any, record) {
              return record.serviceName;
            },
          },
        },
        {
          key: 'subserviceName',
          name: getFormatMessage('subservice_names'),
          tableExtension: { showTooltip: true },
        },

        {
          key: 'status',
          name: getFormatMessage('页面状态'),
          tableExtension: {
            renderRow(_key, val: CommonStatusStep) {
              if (val === CommonStatusStep.Resolve) {
                return getFormatMessage('opened');
              }
              if (val === CommonStatusStep.Reject) {
                return getFormatMessage('closed');
              }
              return '';
            },
          },
        },
        {
          name: getFormatMessage('last_modify_time_local'),
          key: 'ModifyTime',
          tableExtension: {
            renderRow: (_text: string, _val: any, record) => {
              return (
                <div>
                  <div>
                    {getLocalTime(
                      record.service?.ModifyTime,
                      userInfo?.UserSettings?.Timezone
                    )}
                  </div>
                  <div>
                    {getUserNameBySite(record.service?.ModifierUserID, site)}
                  </div>
                </div>
              );
            },
          },
        },
        {
          key: 'operation',
          name: getFormatMessage('operate'),
          tableExtension: {
            renderRow(key, val, record) {
              const text =
                record.status === CommonStatusStep.Resolve
                  ? getFormatMessage('close')
                  : getFormatMessage('open');
              return (
                <AdminPermCheck
                  contentType="action"
                  permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                  placement="bottom"
                >
                  <CommonButton
                    onClick={() => handleOpenClose(record)}
                    type="link"
                  >
                    {text}
                  </CommonButton>
                </AdminPermCheck>
              );
            },
          },
        },
      ]}
    />
  );
};
